// kanjo/Auth.jsx — sign-in surface for unauthenticated users.
//
// Two modes:
//   1. Pre-redirect: render provider buttons; tapping calls kanjo.auth.signIn().
//   2. Post-redirect: the URL carries ?code & state from Cognito. We surface a
//      blocking "Signing you in…" state while completeSignIn() exchanges code
//      for tokens, then route to /today on success.
//
// The guard in main.jsx redirects to /auth whenever currentClaims() is null,
// so this screen is the single entry point for new and returning users.

function AuthScreen({ dark = false }) {
  const [phase, setPhase] = React.useState(() => {
    const url = new URL(window.location.href);
    return url.searchParams.get('code') ? 'completing' : 'idle';
  });
  const [error, setError] = React.useState(null);
  const nav = React.useContext(KanjoNav);

  React.useEffect(() => {
    if (phase !== 'completing') return;
    let cancelled = false;
    (async () => {
      try {
        await window.kanjo.auth.completeSignIn();
        if (cancelled) return;
        // Funnel: a completed OAuth round-trip that began at "Create account"
        // is a signup_complete (sendBeacon survives the reload below). Ordinary
        // sign-ins carry no intent flag and are not counted as new signups.
        try {
          if (sessionStorage.getItem('kanjoSignupIntent')) {
            window.kanjo.track('signup_complete');
            sessionStorage.removeItem('kanjoSignupIntent');
          }
        } catch { /* ignore */ }
        // Move the "not accounting software" acknowledgment off this device and
        // into the user's profile, now that there IS a user to attach it to.
        try { await window.kanjo.disclaimer.stampProfile(); } catch { /* non-fatal */ }
        // Land on Today via a full reload. A same-route hash change alone does
        // NOT re-trigger the auth gate (route is already '/today' by default, so
        // setRoute is a no-op and the check() effect never re-runs), leaving the
        // spinner stuck until a manual refresh. Reloading re-evaluates the gate
        // with the freshly stored tokens — and lets the team shared-snapshot
        // sync run from a clean boot.
        if (window.location.hash !== '#/today') window.location.hash = '/today';
        window.location.reload();
      } catch (e) {
        if (cancelled) return;
        console.warn('[Auth] completeSignIn failed', e);
        setError(e.message || 'Sign-in failed. Try again.');
        setPhase('idle');
        // Strip the bad ?code so the next attempt is clean.
        const url = new URL(window.location.href);
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        url.searchParams.delete('error');
        history.replaceState({}, '', url.pathname + url.hash);
      }
    })();
    return () => { cancelled = true; };
  }, [phase]);

  // A pending team invite (stashed by main.jsx before the OAuth round-trip)
  // means this is a new teammate who must CREATE an account to join.
  const [hasInvite] = React.useState(() => {
    try { return !!sessionStorage.getItem('kanjoPendingInvite'); } catch { return false; }
  });

  // Email handed off from the /p rolodex "save your list" flow, so we can
  // pre-fill the Cognito form instead of asking for it twice.
  const claimEmail = () => {
    try { return window.kanjo?.scale?.rolodexClaim?.getPendingClaimEmail?.() || null; }
    catch { return null; }
  };

  const startSignIn = (provider) => {
    setError(null);
    setPhase('redirecting');
    try {
      window.kanjo.auth.signIn(provider, claimEmail());
    } catch (e) {
      console.warn('[Auth] signIn failed', e);
      setError(e.message || 'Could not start sign-in.');
      setPhase('idle');
    }
  };

  // Creating an account means acknowledging what these books are NOT. The gate
  // sits in front of the Cognito redirect because after it there is no moment
  // left where the person has not already signed up.
  const [showDisclaimer, setShowDisclaimer] = React.useState(false);
  // Read-only view of the same text, reachable from the login screen without
  // starting a signup.
  const [showInfo, setShowInfo] = React.useState(false);

  const startSignUp = () => {
    setError(null);
    const needs = (() => {
      try { return window.kanjo.disclaimer.needsAcknowledgment(); } catch { return true; }
    })();
    if (needs) { setShowDisclaimer(true); return; }
    doSignUp();
  };

  const doSignUp = () => {
    setPhase('redirecting');
    // Funnel: signup intent. Stash a flag that survives the Cognito round-trip so
    // the completing effect can tell a genuine sign-up from an ordinary sign-in.
    try { sessionStorage.setItem('kanjoSignupIntent', '1'); } catch { /* ignore */ }
    try { window.kanjo.track('signup_start'); } catch { /* ignore */ }
    try {
      window.kanjo.auth.signUp(null, claimEmail());
    } catch (e) {
      console.warn('[Auth] signUp failed', e);
      setError(e.message || 'Could not start sign-up.');
      setPhase('idle');
    }
  };

  const acceptDisclaimer = () => {
    try { window.kanjo.disclaimer.acceptOnDevice(); } catch { /* proceed anyway */ }
    setShowDisclaimer(false);
    doSignUp();
  };

  return (
    <KShell dark={dark} tab="today" noNav={true}>
      <div style={{
        height: '100%', display: 'flex', flexDirection: 'column',
        padding: '40px 28px 32px', justifyContent: 'space-between',
      }}>
        {/* Brand block */}
        <div style={{ marginTop: 12 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16,
            background: 'linear-gradient(155deg, #8E3DB0 0%, #6C2A88 62%, #4A1C5E 100%)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 22, boxShadow: '0 10px 24px -12px rgba(76,29,99,0.55)',
          }}>
            <svg width="30" height="30" viewBox="0 0 120 120" fill="none" stroke="currentColor" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="60" cy="62" r="33"/>
              <path d="M45 73 L57 61 L66 68 L80 48"/>
              <path d="M71 48 H81 V58"/>
            </svg>
          </div>
          <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.1 }}>Kanjo</div>
          <div style={{ fontSize: 15, color: 'var(--on-soft)', marginTop: 8, lineHeight: 1.4, maxWidth: 320 }}>
            Cashflow for restaurant operators. Scan receipts, watch food cost, compare vendors.
          </div>
        </div>

        {/* Sign-in surface */}
        <div>
          {phase === 'completing' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '14px 16px', borderRadius: 12,
              background: 'var(--surface-3)', color: 'var(--ink-2)',
              fontSize: 14, marginBottom: 14,
            }}>
              <Spinner/> Signing you in…
            </div>
          )}
          {error && (
            <ErrorBanner style={{ marginBottom: 14 }}>{error}</ErrorBanner>
          )}

          {hasInvite && phase !== 'completing' && (
            <div style={{
              padding: '12px 14px', borderRadius: 12,
              background: 'var(--brand-bg)', color: 'var(--ink-2)',
              fontSize: 13, marginBottom: 14, lineHeight: 1.5,
            }}>
              You've been invited to view a Kanjo account (read-only). On the next screen tap{' '}
              <b>“Sign up”</b> and register with <b>the email your invite was sent to</b> — you'll join automatically.
            </div>
          )}

          {/* Google / Apple are hidden until those identity providers are configured
              (the pool is COGNITO-only today). Email is the working path. */}
          {hasInvite ? (
            <>
              <button onClick={startSignUp} disabled={phase !== 'idle'} aria-label="Create your account"
                      className="btn lg"
                      style={{ width: '100%', marginBottom: 10, background: 'var(--brand-fill)', color: 'var(--on-brand-fill)', opacity: phase === 'idle' ? 1 : 0.6, cursor: phase === 'idle' ? 'pointer' : 'default' }}>
                Create your account
              </button>
              <button onClick={() => startSignIn(null)} disabled={phase !== 'idle'} aria-label="Sign in"
                      className="btn ghost lg"
                      style={{ width: '100%', opacity: phase === 'idle' ? 1 : 0.6, cursor: phase === 'idle' ? 'pointer' : 'default' }}>
                I already have an account
              </button>
            </>
          ) : (
            <>
              <button onClick={() => startSignIn(null)} disabled={phase !== 'idle'} aria-label="Sign in with email"
                      className="btn lg"
                      style={{ width: '100%', marginBottom: 10, background: 'var(--brand-fill)', color: 'var(--on-brand-fill)', opacity: phase === 'idle' ? 1 : 0.6, cursor: phase === 'idle' ? 'pointer' : 'default' }}>
                Sign in with email
              </button>
              <button onClick={startSignUp} disabled={phase !== 'idle'} aria-label="Create account"
                      className="btn ghost lg"
                      style={{ width: '100%', opacity: phase === 'idle' ? 1 : 0.6, cursor: phase === 'idle' ? 'pointer' : 'default' }}>
                Create account
              </button>
            </>
          )}

          {/* Visible to everyone, signing in or signing up — the person who
              already has an account still needs to know what it isn't. The link
              opens the full text, so it's readable without creating an account
              and re-readable by someone who accepted it months ago. */}
          <div style={{
            marginTop: 18, fontSize: 12, color: 'var(--on-dim)',
            textAlign: 'center', lineHeight: 1.5,
          }}>
            Kanjo is an operating tool, not accounting software — never use it for tax filing or financial statements.<br/>
            <button type="button" onClick={() => setShowInfo(true)}
              style={{ background: 'transparent', border: 'none', padding: '10px 8px 6px', minHeight: 44,
                color: 'var(--ink-2)', font: 'inherit', textDecoration: 'underline', textUnderlineOffset: 3,
                cursor: 'pointer' }}>
              How Kanjo numbers work
            </button>
            <br/>
            By continuing you agree to our terms. Your data is encrypted on this device.
          </div>
        </div>
      </div>

      {showDisclaimer && (
        <DisclaimerGate onAccept={acceptDisclaimer} onCancel={() => setShowDisclaimer(false)} />
      )}
      {showInfo && <DisclaimerInfo onClose={() => setShowInfo(false)} />}
    </KShell>
  );
}

// The one statement of what Kanjo's numbers are, shared by the signup gate and
// the read-anytime sheet behind the login screen's link — so someone who already
// has an account can re-read exactly what they agreed to, and the two can never
// drift into saying different things.
function DisclaimerBody() {
  const point = { display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12 };
  const dot = { flexShrink: 0, width: 5, height: 5, borderRadius: 3, background: 'var(--warning)', marginTop: 7 };
  const body = { fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.5 };
  return (
    <>
      <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 16 }}>
        Kanjo shows you how the week is going — food cost, cashflow, what suppliers
        are charging. It is an <strong>operating tool, not accounting software</strong>.
      </div>

      <div style={point}><span style={dot}/><div style={body}>
        Figures are <strong>estimates</strong>. They come from bank feeds and scanned
        receipts, and are built for speed over precision — a number here can be off
        by a few percent and still be doing its job.
      </div></div>
      <div style={point}><span style={dot}/><div style={body}>
        <strong>Sales are derived from bank deposits.</strong> The date is the deposit
        date, not the day you sold, and the amount is what the processor paid you after
        fees. They describe cash flow, not revenue, and will not match your POS totals.
      </div></div>
      <div style={point}><span style={dot}/><div style={body}>
        <strong>Never use Kanjo for tax filing</strong>, sales-tax returns, financial
        statements, or any return, audit or filing an accountant or tax authority
        relies on.
      </div></div>
      <div style={point}><span style={dot}/><div style={body}>
        It does not replace your bookkeeper, accountant or POS records, and it is
        not a book of record. Keep your real books as you keep them today.
      </div></div>
    </>
  );
}

// Bottom-sheet chrome shared by both uses.
function DisclaimerSheet({ title, label, children }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={label}
         style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.5)',
                  display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div className="card" style={{ width: '100%', maxWidth: 460, margin: 10,
        marginBottom: 'calc(env(safe-area-inset-bottom, 0) + 10px)', padding: 20, borderRadius: 18,
        maxHeight: '86vh', overflowY: 'auto' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', marginBottom: 10 }}>
          {title}
        </div>
        {children}
      </div>
    </div>
  );
}

// Shown once per device, in front of the Cognito redirect. It states what these
// books are NOT before there is an account to attach them to, and requires an
// explicit tick — a notice nobody has to act on is a notice nobody reads.
function DisclaimerGate({ onAccept, onCancel }) {
  const [checked, setChecked] = React.useState(false);
  return (
    <DisclaimerSheet title="Before you start" label="Before you create an account">
      <DisclaimerBody />

      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer',
        padding: '12px 0 16px', minHeight: 44 }}>
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
          style={{ width: 20, height: 20, marginTop: 1, flexShrink: 0, accentColor: 'var(--brand-fill)' }}/>
        <span style={{ fontSize: 13.5, color: 'var(--ink)', lineHeight: 1.45 }}>
          I understand Kanjo is not accounting software and will not use it for tax filing.
        </span>
      </label>

      <button onClick={onAccept} disabled={!checked} className="btn lg"
        style={{ width: '100%', background: 'var(--brand-fill)', color: 'var(--on-brand-fill)',
          opacity: checked ? 1 : 0.5, cursor: checked ? 'pointer' : 'default', marginBottom: 8 }}>
        Create account
      </button>
      <button onClick={onCancel} style={{ width: '100%', padding: '10px', background: 'transparent',
        border: 'none', color: 'var(--ink-3)', fontSize: 14, cursor: 'pointer' }}>
        Cancel
      </button>
    </DisclaimerSheet>
  );
}

// The same text, read-only, for someone who already has an account and wants to
// re-read it from the login screen. No acknowledgment — theirs is already on file.
function DisclaimerInfo({ onClose }) {
  return (
    <DisclaimerSheet title="How Kanjo numbers work" label="How Kanjo numbers work">
      <DisclaimerBody />
      <button onClick={onClose} className="btn lg"
        style={{ width: '100%', marginTop: 6, background: 'var(--brand-fill)', color: 'var(--on-brand-fill)' }}>
        Got it
      </button>
    </DisclaimerSheet>
  );
}

function Spinner() {
  return (
    <svg className="kanjo-spinner" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <circle cx="9" cy="9" r="7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="14 30" opacity="0.7"/>
      <style>{`.kanjo-spinner { transform-origin: 50% 50%; animation: kanjo-spin 0.9s linear infinite; }
@keyframes kanjo-spin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.25h2.92c1.7-1.57 2.68-3.88 2.68-6.61z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.19l-2.92-2.25c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.02-3.71H.96v2.32A9 9 0 009 18z"/>
      <path fill="#FBBC05" d="M3.98 10.71A5.4 5.4 0 013.7 9c0-.59.1-1.17.28-1.71V4.96H.96A9 9 0 000 9c0 1.45.35 2.83.96 4.04l3.02-2.33z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 009 0 9 9 0 00.96 4.96L3.98 7.29C4.68 5.16 6.66 3.58 9 3.58z"/>
    </svg>
  );
}

function AppleMark() {
  return (
    <svg width="16" height="18" viewBox="0 0 16 18" fill="currentColor" aria-hidden="true">
      <path d="M13.2 9.6c0-2 1.6-3 1.7-3-1-1.4-2.4-1.6-2.9-1.6-1.2-.1-2.4.7-3 .7-.7 0-1.6-.7-2.7-.7-1.4 0-2.7.8-3.4 2-1.5 2.5-.4 6.2 1 8.2.7 1 1.5 2.1 2.6 2.1 1 0 1.4-.7 2.7-.7s1.6.7 2.7.7c1.1 0 1.8-1 2.5-2 .8-1.1 1.1-2.3 1.1-2.3-.1-.1-2.3-.9-2.3-3.4zM11.1 3.4c.6-.7 1-1.7.9-2.7-.9 0-1.9.6-2.5 1.3-.5.6-1 1.6-.9 2.6 1 .1 2-.5 2.5-1.2z"/>
    </svg>
  );
}

// DisclaimerInfo is shared with the unlock screen (Lock.jsx): an existing user
// sees THAT screen on every app open and the login screen almost never, so the
// link has to live on both or it is effectively unreachable after signup.
Object.assign(window, { AuthScreen, DisclaimerInfo });
